import { blinkConstants } from '@thoughtspot/blink-constants';
import { getRelativeTime } from '@thoughtspot/date-format-util';
import { translate as t } from '@thoughtspot/i18n';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Link } from '@thoughtspot/radiant-react/widgets/link';
import { CellType } from '@thoughtspot/radiant-react/widgets/table/table-list-view/cell/cell-type-service';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import React from 'react';
import styles from './org.module.scss';
const callosumBasePath = '/callosum/v1';
export var tableColumnHeaders;
(function (tableColumnHeaders) {
    tableColumnHeaders["ORG_NAME"] = "orgName";
    tableColumnHeaders["USERS"] = "users";
    tableColumnHeaders["GROUPS"] = "groups";
    tableColumnHeaders["CREATOR"] = "author";
    tableColumnHeaders["CREATED"] = "created";
    tableColumnHeaders["MORE_OPTION"] = "moreOption";
})(tableColumnHeaders || (tableColumnHeaders = {}));
export var ListActionType;
(function (ListActionType) {
    ListActionType["EDIT"] = "edit";
    ListActionType["DELETE"] = "delete";
})(ListActionType || (ListActionType = {}));
export const sortKeyMap = {
    [tableColumnHeaders.ORG_NAME]: 'DEFAULT',
    [tableColumnHeaders.CREATED]: 'CREATED',
    [tableColumnHeaders.CREATOR]: 'AUTHOR',
};
const OrgNameEle = ({ desc, text, isPrimary }) => {
    return (React.createElement(Vertical, null,
        React.createElement(Horizontal, { className: styles.orgsNameEleContainer },
            React.createElement(Typography, { variant: "p", overrideTag: "span", noMargin: true }, text),
            isPrimary && (React.createElement(Typography, { variant: "footnote", overrideTag: "span", className: styles.orgNamePrimary }, t('ORGS_PRIMARY_LABEL')))),
        desc && (React.createElement(Horizontal, { className: styles.orgsDesc },
            React.createElement(Typography, { variant: "caption", overrideTag: "div", color: Colors.grayLight, ellipsis: { rows: 1 }, tooltipConfig: {
                    showTooltipOnlyOnEllipsis: true,
                    dataTooltipAlign: 'center',
                    dataTooltipContent: desc,
                } }, desc)))));
};
const ActionLinks = ({ links }) => (React.createElement(Horizontal, { className: styles.mainActionLinks }, links.map((link) => (React.createElement(Link, { key: link.id, className: styles.actionLink, onClick: link.onClick, tabIndex: 0, onKeyDown: e => {
        if (e.key === 'Enter') {
            link.onClick();
        }
    } }, link.label)))));
export const DEFAULT_PAGINATION_BATCHSIZE = 20;
export const generateMoreOption = (context, data) => {
    const editOption = {
        id: ListActionType.EDIT,
        label: t('metadataObjectMenuItems.edit.label'),
        onClick: () => {
            context.onRowAction({
                action: ListActionType.EDIT,
                data,
            });
        },
    };
    const deleteOption = {
        id: ListActionType.DELETE,
        label: t('metadataObjectMenuItems.remove.label'),
        onClick: () => {
            context.onRowAction({
                action: ListActionType.DELETE,
                data,
            });
        },
    };
    if (data.id === String(blinkConstants.orgs.MASTER_ID)) {
        return [editOption];
    }
    return [editOption, deleteOption];
};
export const getColumnDefinitions = () => {
    return [
        {
            cellRendererFramework: (params) => {
                const { orgDesc, orgName, id } = params.data;
                return (React.createElement(OrgNameEle, { text: orgName.text, desc: orgDesc.text, isPrimary: id === String(blinkConstants.orgs.MASTER_ID) }));
            },
            headerName: t('org.name'),
            colId: tableColumnHeaders.ORG_NAME,
            field: tableColumnHeaders.ORG_NAME,
            sortable: true,
            initialSort: 'asc',
            flex: 2,
        },
        {
            cellRenderer: CellType[CellType.text],
            headerName: t('orgs.users'),
            colId: tableColumnHeaders.USERS,
            sortable: false,
            flex: 1,
        },
        {
            cellRenderer: CellType[CellType.text],
            headerName: t('orgs.groups'),
            colId: tableColumnHeaders.GROUPS,
            sortable: false,
            flex: 1,
            suppressMovable: true,
        },
        {
            cellRenderer: CellType[CellType.avatar],
            headerName: t('orgs.creator'),
            colId: tableColumnHeaders.CREATOR,
            sortable: true,
            flex: 1,
            suppressMovable: true,
        },
        {
            cellRenderer: CellType[CellType.text],
            headerName: t('orgs.created'),
            colId: tableColumnHeaders.CREATED,
            sortable: true,
            flex: 1,
        },
        {
            cellRendererFramework: (params) => {
                const { context, data } = params;
                const links = generateMoreOption(context, data);
                return React.createElement(ActionLinks, { links: links });
            },
            colId: tableColumnHeaders.MORE_OPTION,
            headerName: '',
            sortable: false,
        },
    ];
};
export var ContentColumnHeader;
(function (ContentColumnHeader) {
    ContentColumnHeader["NAME"] = "name";
    ContentColumnHeader["TYPE"] = "type";
    ContentColumnHeader["ORG"] = "org";
    ContentColumnHeader["MODIFIED"] = "modified";
    ContentColumnHeader["AUTHOR"] = "author";
})(ContentColumnHeader || (ContentColumnHeader = {}));
export const getContentColumnDefinitions = () => {
    return [
        {
            cellRenderer: CellType[CellType.text],
            headerName: t('metadataListPage.captions.NAME'),
            colId: ContentColumnHeader.NAME,
            sortable: false,
            flex: 2,
        },
        {
            cellRenderer: CellType[CellType.text],
            headerName: t('metadataListPage.captions.DATA_SOURCE_TYPE'),
            colId: ContentColumnHeader.TYPE,
            sortable: false,
            flex: 2,
        },
        {
            cellRenderer: CellType[CellType.text],
            headerName: t('primaryNavigation.ORGS'),
            colId: ContentColumnHeader.ORG,
            sortable: false,
            flex: 2,
        },
        {
            cellRenderer: CellType[CellType.text],
            headerName: t('metadataListPage.captions.MODIFIED'),
            colId: ContentColumnHeader.MODIFIED,
            sortable: false,
            flex: 2,
        },
        {
            cellRenderer: CellType[CellType.avatar],
            headerName: t('metadataListPage.captions.AUTHOR'),
            colId: ContentColumnHeader.AUTHOR,
            sortable: true,
            flex: 2,
        },
    ];
};
export const normalizeContentData = (data) => {
    return data.map((item) => {
        return {
            id: item.id,
            type: {
                text: item.type,
            },
            name: {
                text: item.name,
            },
            org: {
                text: 'org',
            },
            modified: {
                text: getRelativeTime(item.modified),
            },
            author: [
                {
                    id: item.author,
                    name: item.authorDisplayName,
                    src: `${callosumBasePath}/image/profile/${item.author}`,
                    isNameLink: true,
                },
            ],
        };
    });
};
export const orgParams = {
    sort: 'DEFAULT',
    sortascending: true,
    batchsize: DEFAULT_PAGINATION_BATCHSIZE,
    offset: 0,
    pattern: '',
};
//# sourceMappingURL=util.js.map